import { createContext, ReactNode, useMemo } from 'react';
import { isWebViewUserAgent } from '@contexts/webview-bridge';
import { getPublicPageToken } from '@utils/token/authTokens';
import { PUBLIC_PAGE_TOKEN } from '@utils/token/constants';
export const defaultFeatureFlagContextValues = {
  isDarkModeEnabled: false,
  isCompanyAuthEnabled: false,
  isQuotaAllocationActive: false,
  isSelectVenueActive: false,
  isMultiInviteActive: false,
  isWebView: false,
  isXceedAccess: false,
  isPublicEventReportActive: false,
  isPublicEventReportPage: false,
  isUserManagementActive: false,
  testCompanyIds: '',
  externalPlatformEnabledIds: '',
  isExternalPlatformSectionEnabled: false,
  isUserResetPasswordEnabled: false,
  isCompanyInfoEnabled: false,
  isCompanyIntegrationsEnabled: false,
  isAddVenueEnabled: false,
  isHideEndingTimeEnabled: false,
  isTicketsLeftThresholdEnabled: false,
  isPerTicketAddonBookingEnabled: false,
  isOfflineSalesPassScopeEnabled: false,
};
export const FeatureFlagContext = createContext(defaultFeatureFlagContextValues);

export const FeatureFlagProvider = ({ children }: { children: ReactNode }) => {
  const isDarkModeEnabled = import.meta.env.VITE_DARK_MODE_ENABLED === 'true';
  const isCompanyAuthEnabled = import.meta.env.VITE_COMPANY_AUTH_ENABLED === 'true';
  const isQuotaAllocationActive = import.meta.env.VITE_SHOW_QUOTA_ALLOCATION === 'true';
  const isMultiInviteActive = import.meta.env.VITE_SHOW_MULTI_INVITE === 'true';
  const isSelectVenueActive = import.meta.env.VITE_SHOW_SELECT_VENUE === 'true';
  const isWebView = isWebViewUserAgent();
  const isXceedAccess = /xceed-access-app/i.test(navigator.userAgent);
  const isPublicEventReportActive = import.meta.env.VITE_SHOW_PUBLIC_EVENT_REPORT === 'true';
  const isUserManagementActive = import.meta.env.VITE_SHOW_USERS_PAGE === 'true';
  const testCompanyIds = import.meta.env.VITE_TEST_COMPANIES_IDS || '';
  const externalPlatformEnabledIds = import.meta.env.VITE_EXTERNAL_PLATFORM_ENABLED_IDS || '';
  const isExternalPlatformSectionEnabled = import.meta.env.VITE_SHOW_EXTERNAL_PLATFORM_SECTION === 'true';
  const isCompanyInfoEnabled = import.meta.env.VITE_SHOW_COMPANY_INFO === 'true';
  const isCompanyIntegrationsEnabled = import.meta.env.VITE_SHOW_COMPANY_INTEGRATIONS === 'true';
  const isPublicEventReportPage = !!getPublicPageToken(PUBLIC_PAGE_TOKEN);
  const isUserResetPasswordEnabled = import.meta.env.VITE_SHOW_USER_RESET_PASSWORD === 'true';
  const isAddVenueEnabled = import.meta.env.VITE_SHOW_ADD_VENUE === 'true';
  const isHideEndingTimeEnabled = import.meta.env.VITE_SHOW_HIDE_ENDING_TIME === 'true';
  const isTicketsLeftThresholdEnabled = import.meta.env.VITE_SHOW_TICKETS_LEFT_THRESHOLD === 'true';
  const isPerTicketAddonBookingEnabled = import.meta.env.VITE_SHOW_PER_TICKET_ADDON_BOOKING === 'true';
  const isOfflineSalesPassScopeEnabled = import.meta.env.VITE_SHOW_OFFLINE_SALES_PASS_SCOPE === 'true';
  const featureFlagContextValues = useMemo(
    () => ({
      isDarkModeEnabled,
      isCompanyAuthEnabled,
      isQuotaAllocationActive,
      isMultiInviteActive,
      isSelectVenueActive,
      isWebView,
      isXceedAccess,
      isPublicEventReportActive,
      isPublicEventReportPage,
      isUserManagementActive,
      testCompanyIds,
      externalPlatformEnabledIds,
      isExternalPlatformSectionEnabled,
      isUserResetPasswordEnabled,
      isCompanyInfoEnabled,
      isCompanyIntegrationsEnabled,
      isAddVenueEnabled,
      isHideEndingTimeEnabled,
      isTicketsLeftThresholdEnabled,
      isPerTicketAddonBookingEnabled,
      isOfflineSalesPassScopeEnabled,
    }),
    [
      isDarkModeEnabled,
      isCompanyAuthEnabled,
      isQuotaAllocationActive,
      isMultiInviteActive,
      isSelectVenueActive,
      isWebView,
      isXceedAccess,
      isPublicEventReportActive,
      isPublicEventReportPage,
      isUserManagementActive,
      testCompanyIds,
      externalPlatformEnabledIds,
      isExternalPlatformSectionEnabled,
      isUserResetPasswordEnabled,
      isCompanyInfoEnabled,
      isCompanyIntegrationsEnabled,
      isAddVenueEnabled,
      isHideEndingTimeEnabled,
      isTicketsLeftThresholdEnabled,
      isPerTicketAddonBookingEnabled,
      isOfflineSalesPassScopeEnabled,
    ]
  );
  return <FeatureFlagContext.Provider value={featureFlagContextValues}>{children}</FeatureFlagContext.Provider>;
};
